import { useState, useRef, useEffect } from 'react';
import api from '../api';

const KEY_METRICS = new Set(['shows','ctr','conv_cart','conv_order','buyout_pct','position','rating_rev','orders_per_day']);

const METRICS_MAIN = [
  { key: 'shows',            label: 'Показы',                     fmt: v => Number(v).toLocaleString('ru-RU'), higher: true  },
  { key: 'visits',           label: 'Переходы в карточку',        fmt: v => Number(v).toLocaleString('ru-RU'), higher: true  },
  { key: 'ctr',              label: 'CTR %',                      fmt: v => v + '%',                           higher: true  },
  { key: 'cart',             label: 'Добавл. в корзину',          fmt: v => Number(v).toLocaleString('ru-RU'), higher: true  },
  { key: 'conv_cart',        label: 'Конв. переход→корзина %',    fmt: v => v + '%',                           higher: true  },
  { key: 'orders',           label: 'Заказы',                     fmt: v => Number(v).toLocaleString('ru-RU'), higher: true  },
  { key: 'conv_order',       label: 'Конв. корзина→заказ %',      fmt: v => v + '%',                           higher: true  },
  { key: 'buyouts',          label: 'Выкупы',                     fmt: v => Number(v).toLocaleString('ru-RU'), higher: true  },
  { key: 'buyout_pct',       label: '% выкупа',                   fmt: v => v + '%',                           higher: true  },
  { key: 'cancels',          label: 'Отмены',                     fmt: v => Number(v).toLocaleString('ru-RU'), higher: false },
  { key: 'reviews',          label: 'Отзывы',                     fmt: v => Number(v).toLocaleString('ru-RU'), higher: true  },
  { key: 'delivery',         label: 'Время доставки (дн)',        fmt: v => v,                                 higher: false },
  { key: 'position',         label: 'Позиция в поиске',           fmt: v => Number(v).toLocaleString('ru-RU'), higher: false },
  { key: 'rating',           label: 'Рейтинг карточки',           fmt: v => v,                                 higher: true  },
  { key: 'rating_rev',       label: 'Рейтинг по отзывам',         fmt: v => v,                                 higher: true  },
  { key: 'price_median',     label: 'Медианная цена покупателя',  fmt: v => isNaN(Number(v)) ? v : Number(v).toLocaleString('ru-RU') + ' ₽', higher: false },
  { key: 'orders_per_day',   label: 'Ср. продаж в день, шт',      fmt: v => v,                                                                  higher: true  },
];

const getStorageKey = (auditId) => auditId ? `bystats_competitor_data_${auditId}` : 'bystats_competitor_data';

export default function CompetitorTable({ auditId, mpData: mpDataProp, onMpDataChange, competitorState, onStateChange, myArticles, tableWidth, cardNmIds, allCardNmIds }) {
  const STORAGE_KEY = getStorageKey(auditId);
  const [usDataList, setUsDataList]  = useState(() => competitorState?.usDataList?.length ? competitorState.usDataList : []);
  const [compData, setCompData]     = useState(() => competitorState?.compData?.length ? competitorState.compData : null);
  const [competitorNmIds, setCompetitorNmIds] = useState(competitorState?.competitorNmIds || mpDataProp?.__comp_nm_ids || []);
  const [queriesData, setQueriesData] = useState(null);
  const [queryNmIds, setQueryNmIds] = useState([]);
  const [ourNmIds, setOurNmIds]     = useState([]);
  const [loading, setLoading]       = useState({ us: false, comp: false });
  const [collapsed, setCollapsed]   = useState(false);
  const [compNames, setCompNames]   = useState(() => competitorState?.compNames?.length ? competitorState.compNames : ['','','','','']);
  const [tab, setTab]               = useState('metrics');
  const usFileRef   = useRef(null);
  const compFileRef = useRef(null);
  const [mpData, setMpDataLocal] = useState(() => {
    return mpDataProp && Object.keys(mpDataProp).length > 0 ? mpDataProp : {};
  });
  // Обновляем mpData когда приходит новый пропс из БД
  useEffect(() => {
    if (mpDataProp && Object.keys(mpDataProp).length > 0) {
      setMpDataLocal(mpDataProp);
    }
  }, [JSON.stringify(mpDataProp)]);

  const setMpData = (updater) => {
    setMpDataLocal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (onMpDataChange) onMpDataChange(next, competitorNmIds);
      return next;
    });
  };

  // Синхронизируем с пропсом когда он приходит
  const [stateLoaded, setStateLoaded] = useState(false);
  useEffect(() => {
    if (stateLoaded) return;
    if (!competitorState || !Object.keys(competitorState).length) return;
    if (competitorState.usDataList?.length) setUsDataList(competitorState.usDataList);
    if (competitorState.compData?.length) setCompData(competitorState.compData);
    if (competitorState.compNames?.length) setCompNames(competitorState.compNames);
    if (competitorState.competitorNmIds?.length) setCompetitorNmIds(competitorState.competitorNmIds);
    // Также загружаем mpData из пропса
    if (mpDataProp && Object.keys(mpDataProp).filter(k=>k!=='__comp_nm_ids').length > 0) {
      setMpDataLocal(prev => ({...mpDataProp, ...prev}));
    }
    setStateLoaded(true);
  }, [competitorState, stateLoaded]);

  const getVendorCode = (nmId) => {
    if (!myArticles || !nmId) return null;
    const a = myArticles.find(x => String(x.nm_id) === String(nmId));
    return a ? a.vendor_code : null;
  };

  const saveState = (overrides = {}) => {
    if (onStateChange) onStateChange({
      usDataList, compData, compNames, competitorNmIds, ...overrides
    });
  };

  // Автосохранение state при изменении (только после загрузки)
  const saveTimer = useRef(null);
  const userChanged = useRef(false);
  useEffect(() => {
    if (!onStateChange) return;
    if (!stateLoaded) return;
    if (!usDataList.length && !compData) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      onStateChange({ usDataList, compData, compNames, competitorNmIds });
    }, 3000);
  }, [usDataList, compData, compNames, competitorNmIds]);

  // Источник истины — серверный audit через props.
  // localStorage намеренно не используется, чтобы данные были одинаковыми
  // на всех компьютерах одного аккаунта.

  const parseFile = async (file, isUs) => {
    const key = isUs ? 'us' : 'comp';
    setLoading(p => ({...p, [key]: true}));
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (isUs) {
        // Наш файл — воронка продаж WB
        const r = await api.post('/strategies/parse-funnel', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.error) { alert('Ошибка: ' + r.data.error); return; }
        const newItem = { data: r.data?.data || {}, nmId: String(r.data?.nmId || '') };
        userChanged.current = true;
        const newNmId = String(r.data?.nmId || '');
        setUsDataList(prev => {
          const exists = prev.findIndex(u => String(u.nmId) === newNmId);
          if (exists >= 0) { const updated = [...prev]; updated[exists] = newItem; return updated; }
          return [...prev, newItem];
        });
        setOurNmIds(prev => prev.includes(newNmId) ? prev : [...prev, newNmId]);
        // Автозаполняем Продажи руб. в МПСтатс
        if (r.data?.nmId && r.data?.data?.orders_sum != null) {
          const key = 'sales_rub_' + r.data.nmId;
          setMpData(prev => ({ ...prev, [key]: String(Math.round(r.data.data.orders_sum)) }));
        }
      } else {
        // Файл конкурентов — сравнение карточек WB
        const r = await api.post('/strategies/parse-competitor', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        if (r.data?.error) { alert('Ошибка: ' + r.data.error); return; }
        const newComp = r.data?.competitors || [];
        const newNames = r.data?.nmIds ? r.data.nmIds.map((id,i) => id ? `#${id}` : `К${i+1}`) : compNames;
        const newNmIds = r.data?.nmIds || [];
        userChanged.current = true;
        setCompData(newComp);
        if (r.data?.nmIds) { setCompNames(newNames); setCompetitorNmIds(newNmIds); }
        saveState({ compData: newComp, compNames: newNames, competitorNmIds: newNmIds });
        if (r.data?.queries) { setQueriesData(prev => prev || r.data.queries); setQueryNmIds(r.data.queryNmIds || []); }
      }
    } catch(err) { alert('Ошибка: ' + (err.response?.data?.error || err.message)); }
    finally { setLoading(p => ({...p, [key]: false})); }
  };

  const clear = () => {
    setUsDataList([]); setCompData(null); setCompetitorNmIds([]); setQueriesData(null);
    setQueryNmIds([]); setCompNames(['','','','','']); setOurNmIds([]);
  };

  const getColor = (metric, value, allValues) => {
    if (value === null || value === undefined || value === '') return 'transparent';
    const nums = allValues.filter(v => v !== null && v !== undefined && v !== '').map(Number).filter(v => !isNaN(v));
    if (nums.length < 2) return 'transparent';
    const best  = metric.higher ? Math.max(...nums) : Math.min(...nums);
    const worst = metric.higher ? Math.min(...nums) : Math.max(...nums);
    const num = Number(value);
    if (!KEY_METRICS.has(metric.key)) return 'transparent';
    if (num === best)  return 'rgba(16,185,129,0.22)';
    if (num === worst) return 'transparent';
    return 'transparent';
  };

  const competitors = compData || [];
  const columns = [
    ...usDataList.map((u, i) => ({ label: i===0?'МЫ':`МЫ ${i+1}`, data: u.data||{}, isUs: true, nmId: String(u.nmId) })),
    ...competitors.map((comp, i) => { const nmId = String(competitorNmIds[i] || (cardNmIds && cardNmIds[i]) || `comp_${i}`); const label = compNames[i] || (nmId.startsWith('comp_') ? `К${i+1}` : `#${nmId}`); return { label, data: comp||{}, isUs: false, nmId }; })
  ];
  const hasData = usDataList.length > 0 || (compData && compData.length > 0);

  const th = (label, isUs) => ({
    textAlign: 'center', padding: '8px 12px',
    color: isUs ? '#10b981' : 'rgba(200,215,235,0.55)',
    fontWeight: 700, fontSize: 11, whiteSpace: 'normal', wordBreak: 'break-word',
    width: 150, minWidth: 150, borderLeft: '1px solid rgba(255,255,255,0.05)'
  });

  return (
    <div style={{ marginBottom:16, background:'#090d16', border:'1px solid rgba(255,255,255,0.09)', borderRadius:12, overflow:'hidden' }}>
      {/* Шапка */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'11px 16px', borderBottom: hasData&&!collapsed?'1px solid rgba(255,255,255,0.07)':'none', flexWrap:'wrap', gap:8 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <button onClick={()=>setCollapsed(v=>!v)} style={{ background:'transparent', border:'none', color:'rgba(200,215,235,0.4)', cursor:'pointer', fontSize:13, padding:0 }}>
            {collapsed?'▶':'▼'}
          </button>
          <span style={{ fontSize:14, fontWeight:700, color:'#60a5fa' }}>📊 Анализ конкурентов</span>
          {hasData && <span style={{ fontSize:10, color:'rgba(200,215,235,0.3)' }}>🟢 лучший · 🔴 худший</span>}
        </div>
        <div style={{ display:'flex', gap:6, alignItems:'center', flexWrap:'wrap' }}>
          <button onClick={()=>usFileRef.current.click()}
            style={{ padding:'4px 10px', fontSize:11, fontWeight:600, borderRadius:6, cursor:'pointer', background:'rgba(16,185,129,0.13)', border:'1px solid rgba(16,185,129,0.28)', color:'#10b981', whiteSpace:'nowrap' }}>
            {loading.us?'⟳':'📁 + Наш товар'}
          </button>
          <input ref={usFileRef} type="file" accept=".xlsx" style={{display:'none'}} onChange={e=>{console.log('file selected:', e.target.files[0]?.name, e.target.files[0]?.size); if(e.target.files[0])parseFile(e.target.files[0],true);e.target.value='';}} />
          {usDataList.length > 0 && <button onClick={()=>{setUsDataList([]);setOurNmIds([]);}} style={{ padding:'4px 8px', fontSize:11, borderRadius:6, cursor:'pointer', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', color:'#ef4444' }}>✕ Наши</button>}
          <button onClick={()=>compFileRef.current.click()}
            style={{ padding:'4px 10px', fontSize:11, fontWeight:600, borderRadius:6, cursor:'pointer', background:'rgba(59,130,246,0.13)', border:'1px solid rgba(59,130,246,0.28)', color:'#60a5fa', whiteSpace:'nowrap' }}>
            {loading.comp?'⟳':'📁 Конкуренты'}
          </button>
          <input ref={compFileRef} type="file" accept=".xlsx" style={{display:'none'}} onChange={e=>{if(e.target.files[0])parseFile(e.target.files[0],false);e.target.value='';}}/>
          {hasData&&<button onClick={clear} style={{ padding:'4px 10px', fontSize:11, fontWeight:600, borderRadius:6, cursor:'pointer', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.25)', color:'#ef4444', whiteSpace:'nowrap' }}>🗑 Очистить</button>}
        </div>
      </div>

      {!collapsed && (!hasData ? (
        <div style={{ padding:'28px 16px', textAlign:'center', color:'rgba(200,215,235,0.2)', fontSize:12 }}>
          Загрузите файлы «Сравнение карточек» из WB Аналитики
        </div>
      ) : (
        <>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 16px', borderBottom:'1px solid rgba(255,255,255,0.05)', flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', gap:4 }}>
              {[['metrics','📈 Метрики']].map(([t,l])=>(
                <button key={t} onClick={()=>setTab(t)} style={{ padding:'4px 12px', fontSize:11, fontWeight:600, borderRadius:5, cursor:'pointer', background:tab===t?'rgba(59,130,246,0.2)':'transparent', border:tab===t?'1px solid rgba(59,130,246,0.4)':'1px solid rgba(255,255,255,0.08)', color:tab===t?'#60a5fa':'rgba(200,215,235,0.4)' }}>{l}</button>
              ))}
            </div>
            {compData&&(
              <div style={{ display:'flex', gap:5, flexWrap:'wrap', alignItems:'center' }}>
                <span style={{ fontSize:10, color:'rgba(200,215,235,0.3)' }}>Имена:</span>
                {compNames.slice(0,competitors.length).map((name,i)=>(
                  <input key={i} value={name} onChange={e=>setCompNames(p=>{const n=[...p];n[i]=e.target.value;return n;})} placeholder={`К${i+1}`}
                    style={{ background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:5, color:'rgba(200,215,235,0.7)', fontSize:11, padding:'3px 7px', width:85, outline:'none' }}/>
                ))}
              </div>
            )}
          </div>

          {tab==='metrics'&&(
            <div style={{ overflowX:'auto' }}>
              <table style={{ borderCollapse:'collapse', tableLayout:'fixed', minWidth: tableWidth || 800 }}>
                <thead>
                  <tr style={{ background:'rgba(255,255,255,0.03)' }}>
                    <th style={{ textAlign:'left', padding:'8px 16px', color:'rgba(200,215,235,0.4)', fontWeight:600, fontSize:10, width:200, minWidth:200, position:'sticky', whiteSpace:'normal', wordBreak:'break-word', left:0, background:'#090d16', zIndex:1 }}>МЕТРИКА</th>
                    {columns.map((col,i)=><th key={i} style={th(col.label,col.isUs)}>{col.isUs?<>⭐ МОЙ ТОВАР<br/><span style={{fontSize:9,fontWeight:400,opacity:0.7}}>{getVendorCode(col.nmId)||''} {col.nmId}</span></>:col.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {METRICS_MAIN.map((metric,mi)=>{
                    const allVals = columns.map(col=>col.data[metric.key]);
                    return (
                      <tr key={metric.key} style={{ borderTop:'1px solid rgba(255,255,255,0.04)', background:mi%2===0?'transparent':'rgba(255,255,255,0.012)' }}>
                        <td style={{ padding:'7px 16px', color: KEY_METRICS.has(metric.key)?'rgba(16,185,129,0.8)':'rgba(200,215,235,0.5)', fontSize:11, whiteSpace:'nowrap', position:'sticky', left:0, background: KEY_METRICS.has(metric.key)?(mi%2===0?'rgba(16,185,129,0.06)':'rgba(16,185,129,0.09)'):(mi%2===0?'#090d16':'#0b1019'), zIndex:1, fontWeight: KEY_METRICS.has(metric.key)?600:400 }}>{metric.label}</td>
                        {columns.map((col,ci)=>{
                          const val=col.data[metric.key];
                          const bg=getColor(metric,val,allVals);
                          const txt=val!==null&&val!==undefined&&val!==''?(() => { try { return metric.fmt(val); } catch { return val; } })():'—';
                          return <td key={ci} style={{ textAlign:'center', padding:'7px 12px', background:bg, color:col.isUs?'rgba(210,240,225,0.95)':'rgba(200,215,235,0.82)', fontWeight:col.isUs?700:400, borderLeft:'1px solid rgba(255,255,255,0.04)', fontSize:12 }}>{txt}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {tab==='queries'&&(
            !queriesData||queriesData.length===0 ? (
              <div style={{ padding:'24px 16px', textAlign:'center', color:'rgba(200,215,235,0.25)', fontSize:12 }}>
                Загрузите файл — запросы подтянутся автоматически
              </div>
            ) : (
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse' }}>
                  <thead>
                    <tr style={{ background:'rgba(255,255,255,0.03)' }}>
                      <th style={{ textAlign:'left', padding:'8px 16px', color:'rgba(200,215,235,0.4)', fontWeight:600, fontSize:10, width:200, minWidth:200, position:'sticky', whiteSpace:'normal', wordBreak:'break-word', left:0, background:'#090d16', zIndex:1 }}>ЗАПРОС</th>
                      <th style={{ textAlign:'center', padding:'8px 12px', color:'rgba(200,215,235,0.4)', fontWeight:600, fontSize:10, minWidth:100, borderLeft:'1px solid rgba(255,255,255,0.05)' }}>ЧАСТОТА</th>
                      {queryNmIds.map((nmId,i)=>(
                        <th key={i} style={{ textAlign:'center', padding:'8px 12px', color: ourNmIds.includes(nmId)?'#10b981':'rgba(200,215,235,0.55)', fontWeight:700, fontSize:10, minWidth:90, borderLeft:'1px solid rgba(255,255,255,0.05)', wordBreak:'break-word', whiteSpace:'normal' }}>
                          {ourNmIds.includes(nmId)?`⭐ ${nmId}`:compNames[i]||`#${nmId}`}<br/>
                          <span style={{ fontSize:9, fontWeight:400, opacity:0.6 }}>конв.корзина %</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {queriesData.slice(0,100).map((q,qi)=>{
                      const convVals = queryNmIds.map(nmId=>q[`conv_cart_${nmId}`]).filter(v=>v!==null&&v!==undefined).map(Number).filter(v=>!isNaN(v));
                      const bestConv = convVals.length>1?Math.max(...convVals):null;
                      const worstConv = convVals.length>1?Math.min(...convVals):null;
                      return (
                        <tr key={qi} style={{ borderTop:'1px solid rgba(255,255,255,0.04)', background:qi%2===0?'transparent':'rgba(255,255,255,0.012)' }}>
                          <td style={{ padding:'6px 16px', color:'rgba(200,215,235,0.8)', fontSize:11, position:'sticky', left:0, background:qi%2===0?'#090d16':'#0b1019', zIndex:1, whiteSpace:'nowrap' }}>{q.query}</td>
                          <td style={{ textAlign:'center', padding:'6px 12px', fontSize:11, color:'rgba(200,215,235,0.5)', borderLeft:'1px solid rgba(255,255,255,0.04)' }}>{Number(q.freq).toLocaleString('ru-RU')}</td>
                          {queryNmIds.map((nmId,i)=>{
                            const val=q[`conv_cart_${nmId}`];
                            const num=val!==null&&val!==undefined?Number(val):null;
                            const isUs=ourNmIds.includes(nmId);
                            const bg=num!==null&&bestConv!==null?(num===bestConv?'rgba(16,185,129,0.22)':num===worstConv?'rgba(239,68,68,0.18)':'transparent'):'transparent';
                            return <td key={i} style={{ textAlign:'center', padding:'6px 12px', fontSize:11, background:bg, color:isUs?'rgba(210,240,225,0.95)':'rgba(200,215,235,0.7)', fontWeight:isUs?700:400, borderLeft:'1px solid rgba(255,255,255,0.04)' }}>{num!==null?num+'%':'—'}</td>;
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      ))}

    {/* Таблица МПСтатс — ручной ввод */}
    {hasData && (
      <div style={{ marginTop:16, background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:10, overflow:'auto' }}>
        <div style={{ padding:'10px 16px', borderBottom:'1px solid rgba(255,255,255,0.07)', fontSize:12, fontWeight:700, color:'#10b981' }}>📊 Данные МПСтатс (ручной ввод)</div>
        <table style={{ borderCollapse:'collapse', tableLayout:'fixed', minWidth: tableWidth || 800 }}>
          <thead>
            <tr style={{ background:'rgba(255,255,255,0.03)' }}>
              <th style={{ textAlign:'left', padding:'8px 16px', color:'rgba(200,215,235,0.4)', fontWeight:600, fontSize:10, width:200, minWidth:200, position:'sticky', whiteSpace:'normal', wordBreak:'break-word', left:0, background:'#090d16', zIndex:1 }}>ПОКАЗАТЕЛЬ</th>
              {columns.map((col,i) => (
                <th key={i} style={{ textAlign:'center', padding:'8px 12px', color:col.isUs?'#10b981':'rgba(200,215,235,0.55)', fontWeight:700, fontSize:10, width:150, minWidth:150, borderLeft:'1px solid rgba(255,255,255,0.05)' }}>
                  {col.isUs ? <>⭐ МОЙ ТОВАР<br/><span style={{fontSize:9,fontWeight:400,opacity:0.7}}>{getVendorCode(col.nmId)||''} {col.nmId}</span></> : <>{col.label}<br/><span style={{fontSize:9,fontWeight:400,opacity:0.6}}>#{col.nmId}</span></>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { key:'price_wb', label:'Цена с WB кошельком' },
              { key:'sales_rub', label:'Продажи, руб.' },
              { key:'reviews5', label:'Последние 5 отзывов' },
              { key:'sizes', label:'Размеры в наличии' },
            ].map((row, ri) => (
              <tr key={ri} style={{ borderBottom:'1px solid rgba(255,255,255,0.05)', background:ri%2===0?'transparent':'rgba(255,255,255,0.012)' }}>
                <td style={{ padding:'6px 16px', fontSize:11, color:'rgba(200,215,235,0.8)', fontWeight:600, position:'sticky', left:0, background:ri%2===0?'#090d16':'#0b1019', zIndex:1 }}>{row.label}</td>
                {columns.map((col,i) => {
                  const cellKey = `${row.key}_${col.nmId}`;
                  return (
                    <td key={i} style={{ padding:'4px 8px', borderLeft:'1px solid rgba(255,255,255,0.04)' }}>
                      <input
                        value={mpData[cellKey] || ''}
                        onChange={e => setMpData(prev => ({ ...prev, [cellKey]: e.target.value }))}
                        style={{ width:'100%', background:'transparent', border:'none', borderBottom:'1px solid rgba(255,255,255,0.1)', color:col.isUs?'rgba(210,240,225,0.95)':'rgba(200,215,235,0.8)', fontSize:11, padding:'2px 4px', outline:'none', textAlign:'center' }}
                        placeholder="—"
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
  );
}
